import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import { Plus, Receipt, ListChecks, Trash2, ChevronRight, Search, X, Users, FileDown, Filter } from "lucide-react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import SessionsPanel from "@/components/SessionsPanel";
import { CONDITION_META_V2 } from "@/components/app/ToothSVGV2";
import { recommendTreatments, TIER_LABEL, stars } from "@/lib/treatmentRecommender";
import { DateDMYInput } from "@/components/DateDMYInput";

// ---- Small helpers ----------------------------------------------------------
const todayISO = () => new Date().toISOString().slice(0, 10);
const STATUS_STYLES = {
  planned:     "bg-linen text-ink",
  in_progress: "bg-[#FDF3ED] text-[#D06A42]",
  completed:   "bg-[#E8F0EB] text-[#2D6A4F]",
  cancelled:   "bg-[#F5E7E4] text-[#8A2D2D]",
};
const PRIORITY_STYLES = {
  high:   { bg: "#F5E7E4", fg: "#8A2D2D", label: "High" },
  normal: { bg: "#EEEDE7", fg: "#5B6660", label: "Normal" },
  low:    { bg: "#F0EBDD", fg: "#8A6420", label: "Low" },
};

export default function Treatments() {
  const { user } = useAuth();
  const canDelete = user?.role === "admin" || user?.role === "doctor";
  const isAdmin   = user?.role === "admin";
  const isDoctor  = user?.role === "doctor";
  const [searchParams, setSearchParams] = useSearchParams();
  const [treatments, setTreatments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [team, setTeam] = useState([]);
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [customProc, setCustomProc] = useState(false);
  // Dashboard state
  const [tab, setTab]                 = useState("all");
  const [searchQ, setSearchQ]         = useState("");
  const [filterDoctor, setFilterDoctor]   = useState("");
  const [filterProc, setFilterProc]       = useState("");
  const [filterStatus, setFilterStatus]   = useState("");
  const [filterDate, setFilterDate]       = useState("");
  const [filterTooth, setFilterTooth]     = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterMultiVisit, setFilterMultiVisit] = useState(false);
  const [filterLab, setFilterLab]           = useState(false);
  const [pageSize, setPageSize]           = useState(25);
  const [pageIndex, setPageIndex]         = useState(0);
  const [selected, setSelected]           = useState(() => new Set());
  const [showFilters, setShowFilters]     = useState(false);
  const [form, setForm] = useState({
    patient_id: "", tooth: "", catalog_id: "", variant: "",
    procedure: "", cost: 0, status: "planned", priority: "normal", notes: "",
    surfaces: [], diagnosis: "", diagnoses: [], chart_ref: "",
    // Clinical event date — the day the procedure actually happened.
    // Distinct from `created_at` (record-creation timestamp). Doctors can
    // back-date historical treatments here.
    date: todayISO(),
  });

  const load = useCallback(async () => {
    const [t, p, c, tm] = await Promise.all([
      api.get("/treatments"),
      api.get("/patients"),
      api.get("/procedure-catalog"),
      api.get("/team").catch(() => ({ data: [] })),
    ]);
    setTreatments(t.data); setPatients(p.data); setCatalog(c.data); setTeam(tm.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Prefill from URL — used when the Dental Chart's "Add treatment" button
  // navigates here with tooth / surfaces / diagnosis / procedure already
  // selected. Opens the dialog automatically so the doctor's only job is to
  // finish the clinical details.
  useEffect(() => {
    const patient_id = searchParams.get("patient_id");
    const tooth      = searchParams.get("tooth");
    if (!patient_id && !tooth) return;
    const surfacesStr = searchParams.get("surfaces") || "";
    const surfaces = surfacesStr ? surfacesStr.split(",").filter(Boolean) : [];
    const diagnosis = searchParams.get("diagnosis") || "";
    const diagnosesStr = searchParams.get("diagnoses") || "";
    const diagnoses = diagnosesStr ? diagnosesStr.split(",").filter(Boolean) : (diagnosis ? [diagnosis] : []);
    const procedure = searchParams.get("procedure") || "";
    const variant   = searchParams.get("variant") || "";
    const chart_ref = searchParams.get("chart_ref") || "";
    setForm((f) => ({
      ...f,
      patient_id: patient_id || f.patient_id,
      tooth: tooth || f.tooth,
      surfaces,
      diagnosis,
      diagnoses,
      procedure,
      variant,
      chart_ref,
    }));
    setOpen(true);
    // Clear params so a manual refresh doesn't re-open with stale values.
    const next = new URLSearchParams(searchParams);
    ["patient_id", "tooth", "surfaces", "diagnosis", "diagnoses", "procedure", "variant", "chart_ref"].forEach((k) => next.delete(k));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Group catalog by name to detect variants (e.g., all "Crown" entries → variants list)
  const groups = useMemo(() => {
    const map = new Map();
    for (const item of catalog.filter((c) => c.active !== false)) {
      if (!map.has(item.name)) map.set(item.name, []);
      map.get(item.name).push(item);
    }
    return Array.from(map.entries()).map(([name, items]) => ({
      name,
      category: items[0].category,
      items,
      hasVariants: items.some((x) => x.variant) && items.length > 1,
    }));
  }, [catalog]);

  // When user picks a procedure name, auto-select single-variant or ask for choice
  const pickProcedure = (name) => {
    const g = groups.find((x) => x.name === name);
    if (!g) {
      setForm((f) => ({ ...f, procedure: name, catalog_id: "", variant: "", cost: 0 }));
      return;
    }
    if (g.hasVariants) {
      // Show variant selector — clear cost until variant chosen
      setForm((f) => ({ ...f, procedure: name, catalog_id: "", variant: "", cost: 0 }));
    } else {
      const only = g.items[0];
      setForm((f) => ({ ...f, procedure: name, catalog_id: only.id, variant: only.variant || "", cost: only.cost }));
    }
  };

  const pickVariant = (variant) => {
    const g = groups.find((x) => x.name === form.procedure);
    if (!g) return;
    const item = g.items.find((x) => (x.variant || "") === variant);
    if (item) {
      setForm((f) => ({ ...f, variant, catalog_id: item.id, cost: item.cost }));
    }
  };

  const activeVariants = useMemo(() => {
    const g = groups.find((x) => x.name === form.procedure);
    return g?.hasVariants ? g.items : [];
  }, [groups, form.procedure]);

  // Ranked treatment recommendations from the chart's diagnoses. Empty when
  // the New Treatment dialog was opened directly (no chart context).
  const chartRecommendations = useMemo(
    () => (form.diagnoses?.length ? recommendTreatments(form.diagnoses) : []),
    [form.diagnoses]
  );

  // Pick a recommendation with one click — fills procedure, variant and cost.
  const pickRecommendation = (rec) => {
    setCustomProc(false);
    setForm((f) => ({ ...f, procedure: rec.procedure, variant: rec.variant || "", cost: 0 }));
    // catalog hydration is handled by the useEffect below
    setTimeout(() => {
      const g = groups.find((x) => x.name === rec.procedure);
      if (!g) return;
      if (g.hasVariants && rec.variant) {
        const item = g.items.find((x) => (x.variant || "") === rec.variant);
        if (item) setForm((f) => ({ ...f, catalog_id: item.id, variant: item.variant || "", cost: item.cost }));
      } else if (!g.hasVariants) {
        const only = g.items[0];
        setForm((f) => ({ ...f, catalog_id: only.id, variant: only.variant || "", cost: only.cost }));
      }
    }, 0);
  };

  // Once the catalog is loaded, hydrate cost/catalog_id for any URL-prefilled
  // procedure (Dental Chart → Add treatment).
  useEffect(() => {
    if (!catalog.length || !form.procedure || form.cost) return;
    const g = groups.find((x) => x.name === form.procedure);
    if (!g) return;
    if (g.hasVariants && form.variant) {
      const item = g.items.find((x) => (x.variant || "") === form.variant);
      if (item) setForm((f) => ({ ...f, catalog_id: item.id, cost: item.cost }));
    } else if (!g.hasVariants) {
      const only = g.items[0];
      setForm((f) => ({ ...f, catalog_id: only.id, variant: only.variant || "", cost: only.cost }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, groups]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        patient_id: form.patient_id,
        tooth: form.tooth,
        procedure: form.procedure,
        variant: form.variant || null,
        cost: Number(form.cost),
        status: form.status,
        priority: form.priority || "normal",
        notes: form.notes,
        surfaces: form.surfaces || [],
        diagnosis: form.diagnosis || null,
        chart_ref: form.chart_ref || null,
        // Clinical event date — kept separate from the record's created_at.
        // The DateDMYInput emits an ISO yyyy-mm-dd string.
        date: (form.date || todayISO()),
      };
      const { data } = await api.post("/treatments", payload);
      const kit = data.kit_result;
      if (kit?.consumed_items?.length) {
        toast.success(
          `Treatment saved · Auto-deducted ${kit.consumed_items.length} item${kit.consumed_items.length > 1 ? "s" : ""} (₹${kit.material_cost?.toLocaleString("en-IN") || 0})`,
          { duration: 4500 }
        );
        (kit.low_stock_warnings || []).forEach((w) =>
          toast.warning(`Low stock: ${w.item_name} · ${w.new_stock} ${w.unit} left (reorder at ${w.reorder_level})`, { duration: 6000 })
        );
      } else if (kit?.missing_kit) {
        toast.info(
          `Treatment saved · No Treatment Kit set for ${payload.procedure}${payload.variant ? ` (${payload.variant})` : ""}. Create one to auto-track materials.`,
          { duration: 5000 }
        );
      } else if (kit?.auto_deduct_disabled) {
        toast.info(`Treatment saved · Kit '${kit.kit_name}' found but auto-deduct is off.`);
      } else {
        toast.success("Treatment added");
      }
      setOpen(false);
      setCustomProc(false);
      setForm({ patient_id: "", tooth: "", catalog_id: "", variant: "", procedure: "", cost: 0, status: "planned", priority: "normal", notes: "", surfaces: [], diagnosis: "", diagnoses: [], chart_ref: "", date: todayISO() });
      load();
    } catch { toast.error("Failed"); }
  };

  const markCompleted = async (t) => {
    try {
      const { data } = await api.patch(`/treatments/${t.id}`, { status: "completed" });
      const kit = data.kit_result;
      if (kit?.consumed_items?.length) {
        toast.success(
          `Completed · Auto-deducted ${kit.consumed_items.length} item${kit.consumed_items.length > 1 ? "s" : ""} (₹${kit.material_cost?.toLocaleString("en-IN") || 0})`,
          { duration: 4500 }
        );
        (kit.low_stock_warnings || []).forEach((w) =>
          toast.warning(`Low stock: ${w.item_name} · ${w.new_stock} ${w.unit} left`, { duration: 6000 })
        );
      } else if (kit?.missing_kit) {
        toast.info(`Completed · No Treatment Kit set for ${t.procedure}. Create one to auto-track materials.`);
      } else if (kit?.auto_deduct_disabled) {
        toast.info(`Completed · Kit '${kit.kit_name}' found but auto-deduct is off.`);
      } else {
        toast.success("Marked completed");
      }
      load();
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    }
  };

  const pname = (pid) => patients.find((p) => p.id === pid)?.name || "—";
  const dname = (did) => (did ? (team.find((u) => u.id === did)?.name || "—") : "—");

  // ---- Derived: role visibility + filters + tab + search + pagination -----
  const visibleTreatments = useMemo(() => {
    if (isDoctor) {
      return treatments.filter((t) => !t.doctor_id || t.doctor_id === user.id);
    }
    return treatments;
  }, [treatments, isDoctor, user]);

  const counts = useMemo(() => {
    const today = todayISO();
    const planned = visibleTreatments.filter((t) => t.status === "planned").length;
    const inProg  = visibleTreatments.filter((t) => t.status === "in_progress").length;
    const done    = visibleTreatments.filter((t) => t.status === "completed").length;
    const overdue = visibleTreatments.filter((t) => (t.status === "planned" || t.status === "in_progress") && t.date && t.date.slice(0, 10) < today).length;
    return { planned, inProg, done, overdue, total: visibleTreatments.length };
  }, [visibleTreatments]);

  const filtered = useMemo(() => {
    const q = searchQ.trim().toLowerCase();
    const today = todayISO();
    return visibleTreatments.filter((t) => {
      // Tab pre-filter
      if (tab === "today"       && t.date?.slice(0, 10) !== today) return false;
      if (tab === "scheduled"   && !(t.status === "planned" && t.date?.slice(0, 10) >= today)) return false;
      if (tab === "in_progress" && t.status !== "in_progress") return false;
      if (tab === "completed"   && t.status !== "completed") return false;
      if (tab === "cancelled"   && t.status !== "cancelled") return false;
      if (tab === "high"        && t.priority !== "high") return false;
      if (tab === "lab"         && !t.send_to_lab) return false;
      if (tab === "multi"       && (t.sessions_planned || 0) < 2) return false;
      // Filters
      if (filterDoctor && (t.doctor_id || "") !== filterDoctor) return false;
      if (filterProc   && (t.procedure || "").toLowerCase() !== filterProc.toLowerCase()) return false;
      if (filterStatus && t.status !== filterStatus) return false;
      if (filterDate   && (t.date || "").slice(0, 10) !== filterDate) return false;
      if (filterTooth  && String(t.tooth || "") !== filterTooth) return false;
      if (filterPriority && (t.priority || "normal") !== filterPriority) return false;
      if (filterMultiVisit && (t.sessions_planned || 0) < 2) return false;
      if (filterLab    && !t.send_to_lab) return false;
      // Search across patient name / procedure / tooth / notes
      if (q) {
        const hay = `${pname(t.patient_id)} ${t.procedure} ${t.tooth || ""} ${t.notes || ""} ${t.variant || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [visibleTreatments, tab, filterDoctor, filterProc, filterStatus, filterDate, filterTooth, filterPriority, filterMultiVisit, filterLab, searchQ, patients]);

  useEffect(() => { setPageIndex(0); setSelected(new Set()); }, [tab, searchQ, filterDoctor, filterProc, filterStatus, filterDate, filterTooth, filterPriority, filterMultiVisit, filterLab, pageSize]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageStart = pageIndex * pageSize;
  const pageEnd   = Math.min(pageStart + pageSize, filtered.length);
  const paged     = useMemo(() => filtered.slice(pageStart, pageEnd), [filtered, pageStart, pageEnd]);

  const uniqueProcedures = useMemo(
    () => Array.from(new Set(visibleTreatments.map((t) => t.procedure).filter(Boolean))).sort(),
    [visibleTreatments]
  );
  const doctorList = useMemo(() => team.filter((u) => u.role === "doctor"), [team]);

  // ---- Selection helpers --------------------------------------------------
  const toggleRow = (id) => setSelected((prev) => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });
  const toggleAllOnPage = () => setSelected((prev) => {
    const allOn = paged.every((t) => prev.has(t.id));
    const s = new Set(prev);
    paged.forEach((t) => allOn ? s.delete(t.id) : s.add(t.id));
    return s;
  });

  const bulkAssignDoctor = async (docId) => {
    if (!selected.size) return;
    try {
      await Promise.all(Array.from(selected).map((id) => api.patch(`/treatments/${id}`, { doctor_id: docId || null })));
      toast.success(`Doctor ${docId ? "assigned" : "cleared"} on ${selected.size} treatment(s)`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e, "Bulk assign failed")); }
  };
  const bulkStatus = async (status) => {
    if (!selected.size || !status) return;
    try {
      await Promise.all(Array.from(selected).map((id) => api.patch(`/treatments/${id}`, { status })));
      toast.success(`Status changed to ${status.replace("_", " ")}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e, "Bulk status failed")); }
  };
  const bulkPriority = async (priority) => {
    if (!selected.size || !priority) return;
    try {
      await Promise.all(Array.from(selected).map((id) => api.patch(`/treatments/${id}`, { priority })));
      toast.success(`Priority set to ${priority}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e, "Bulk priority failed")); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} selected treatment(s)? This cannot be undone.`)) return;
    try {
      await Promise.all(Array.from(selected).map((id) => api.delete(`/treatments/${id}`)));
      toast.success(`Deleted ${selected.size} treatment(s)`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e, "Bulk delete failed")); }
  };
  const exportCsv = () => {
    const rows = [
      ["Patient", "Procedure", "Variant", "Tooth", "Date", "Doctor", "Status", "Cost"],
      ...filtered.map((t) => [
        pname(t.patient_id), t.procedure, t.variant || "", t.tooth || "",
        t.date?.slice(0, 10) || "", dname(t.doctor_id), t.status, t.cost,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `treatments-${todayISO()}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const generateReceipt = async (t) => {
    try {
      const { data } = await api.post(`/invoices/from-treatment/${t.id}`, {});
      toast.success(`Invoice ${data.invoice_number} created (₹${data.total.toLocaleString("en-IN")})`);
    } catch (err) {
      toast.error(apiError(err, "Failed"));
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <span className="overline text-ink-soft">Treatment records</span>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Every procedure, remembered.</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/settings" data-testid="link-price-list"
            className="text-xs inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-pine/20 hover:bg-linen">
            <ListChecks className="w-3.5 h-3.5" /> Price list
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="btn-new-treatment" className="btn-primary text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add treatment</button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader><DialogTitle className="font-display text-2xl">New treatment</DialogTitle></DialogHeader>
              <form onSubmit={create} className="space-y-3" data-testid="new-treatment-form">
                {(form.diagnoses?.length || form.surfaces?.length || form.chart_ref) && (
                  <div data-testid="tx-chart-context"
                       className="rounded-xl border border-[#E8EAE9] bg-linen/40 px-3 py-2 space-y-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-ink-mid">
                      <span className="uppercase tracking-widest text-[10px] text-ink-soft mr-1">Clinical findings</span>
                      {form.tooth && (
                        <span className="px-2 py-0.5 rounded-full bg-white border border-[#E8EAE9] font-mono">
                          Tooth {form.tooth}
                        </span>
                      )}
                      {form.surfaces?.map((s) => (
                        <span key={s} className="px-2 py-0.5 rounded-full bg-white border border-[#E8EAE9] font-mono">{s}</span>
                      ))}
                      {(form.diagnoses || []).map((code) => (
                        <span key={code}
                              data-testid={`tx-locked-diag-${code}`}
                              className="px-2 py-0.5 rounded-full bg-[#FDE7DF] text-[#8A3F1E] uppercase tracking-widest text-[10px]">
                          ✓ {CONDITION_META_V2[code]?.label || code}
                        </span>
                      ))}
                    </div>
                    <div className="text-[10px] text-ink-soft italic">
                      Diagnoses are locked — edit them from the Dental Chart if needed.
                    </div>
                  </div>
                )}

                {chartRecommendations.length > 0 && (
                  <div data-testid="tx-recommendations" className="rounded-xl border border-[#E8EAE9] bg-white px-3 py-2 space-y-1.5">
                    <div className="text-[10px] uppercase tracking-widest text-ink-soft">Suggested treatments</div>
                    <div className="space-y-1.5">
                      {chartRecommendations.slice(0, 5).map((r) => {
                        const active = form.procedure === r.procedure && (form.variant || "") === (r.variant || "");
                        const tierColor =
                          r.tier === "recommended" ? "#8A3F1E" :
                          r.tier === "alternative" ? "#2C58A6" :
                          r.tier === "long_term"   ? "#3F7A4A" :
                                                     "#5B6660";
                        return (
                          <button
                            type="button"
                            key={`${r.procedure}-${r.variant || ""}`}
                            data-testid={`tx-rec-${r.procedure.toLowerCase().replace(/[^a-z0-9]+/g, "-")}${r.variant ? "-" + r.variant.toLowerCase() : ""}`}
                            onClick={() => pickRecommendation(r)}
                            className={`w-full text-left rounded-lg px-2.5 py-2 border transition ${
                              active ? "border-clay bg-linen" : "border-[#E8EAE9] hover:bg-linen/60"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="w-3.5 h-3.5 rounded-full border grid place-items-center shrink-0"
                                    style={{ borderColor: active ? "#D06A42" : "#D6D9D8" }}>
                                {active && <span className="w-2 h-2 rounded-full bg-clay" />}
                              </span>
                              <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                                    style={{ background: tierColor + "18", color: tierColor }}>
                                {TIER_LABEL[r.tier]}
                              </span>
                              <span className="text-xs font-medium text-ink">
                                {r.procedure}{r.variant ? ` · ${r.variant}` : ""}
                              </span>
                              <span className="ml-auto text-[10px] text-[#D0A94A]">{stars(r.stars)}</span>
                            </div>
                            {r.reason && (
                              <div className="text-[10px] text-ink-mid mt-1 pl-6 leading-snug">{r.reason}</div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      data-testid="tx-custom-toggle"
                      onClick={() => { setCustomProc((v) => !v); setForm((f) => ({ ...f, procedure: "", variant: "", catalog_id: "", cost: 0 })); }}
                      className="w-full text-left text-[11px] text-ink-mid hover:text-ink px-2.5 py-1.5 rounded-lg border border-dashed border-[#D6D9D8] hover:border-clay/50">
                      {customProc ? "− Use the price list instead" : "+ Create custom treatment"}
                    </button>
                  </div>
                )}

                <select required data-testid="tx-patient" value={form.patient_id}
                  onChange={(e) => setForm({ ...form, patient_id: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                  <option value="">Select patient</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>

                <div>
                  <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Procedure</label>
                  {customProc ? (
                    <input
                      data-testid="tx-procedure-custom"
                      required
                      autoFocus
                      value={form.procedure}
                      onChange={(e) => setForm((f) => ({ ...f, procedure: e.target.value, catalog_id: "", variant: "" }))}
                      placeholder="e.g. Laser Gingivectomy, Occlusal Adjustment…"
                      className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white"
                    />
                  ) : (
                    <select required data-testid="tx-procedure"
                      value={form.procedure}
                      onChange={(e) => pickProcedure(e.target.value)}
                      className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                      <option value="">Pick from price list…</option>
                      {["Preventive","Restorative","Endodontic","Prosthetic","Surgical","Cosmetic","Other"].map((cat) => {
                        const cg = groups.filter((g) => g.category === cat);
                        if (cg.length === 0) return null;
                        return (
                          <optgroup key={cat} label={cat}>
                            {cg.map((g) => <option key={g.name} value={g.name}>{g.name}</option>)}
                          </optgroup>
                        );
                      })}
                    </select>
                  )}
                  {/* Custom-procedure toggle is always available, even when
                      there are no chart recommendations. */}
                  {chartRecommendations.length === 0 && (
                    <button
                      type="button"
                      data-testid="tx-custom-toggle"
                      onClick={() => { setCustomProc((v) => !v); setForm((f) => ({ ...f, procedure: "", variant: "", catalog_id: "", cost: 0 })); }}
                      className="mt-1.5 text-[11px] text-ink-mid hover:text-ink underline">
                      {customProc ? "Use the price list instead" : "+ Create custom treatment"}
                    </button>
                  )}
                </div>

                {activeVariants.length > 0 && (
                  <div data-testid="tx-variant-picker">
                    <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-2 block">Variant</label>
                    <div className="flex gap-2 flex-wrap">
                      {activeVariants.map((v) => {
                        const label = v.variant || "Standard";
                        const active = form.variant === (v.variant || "");
                        return (
                          <button type="button" key={v.id}
                            data-testid={`tx-variant-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                            onClick={() => pickVariant(v.variant || "")}
                            className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                              active ? "bg-pine text-linen border-pine" : "border-[#E8EAE9] hover:border-pine/40"
                            }`}>
                            {label} · <span className="font-mono">₹{v.cost.toLocaleString("en-IN")}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="tx-tooth" placeholder="Tooth # (1-32)" value={form.tooth}
                    onChange={(e) => setForm({ ...form, tooth: e.target.value })}
                    className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft text-sm">₹</span>
                    <input required data-testid="tx-cost" type="number" min={0} placeholder="Cost"
                      value={form.cost}
                      onChange={(e) => setForm({ ...form, cost: e.target.value })}
                      className="w-full rounded-xl border border-[#E8EAE9] pl-7 pr-4 py-2.5 text-sm" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <select data-testid="tx-status" value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                    <option value="planned">Planned</option>
                    <option value="in_progress">In progress</option>
                    <option value="completed">Completed</option>
                  </select>
                  <select data-testid="tx-priority" value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
                    <option value="normal">Normal priority</option>
                    <option value="high">High priority</option>
                    <option value="low">Low priority</option>
                  </select>
                </div>

                {/* Treatment Date — the clinical event date. Doctors can
                    back-date historical procedures. Distinct from the row's
                    created_at record-creation timestamp. */}
                <div>
                  <label htmlFor="tx-date" className="block text-xs font-medium text-ink-soft mb-1">
                    Treatment Date <span className="text-red-500">*</span>
                  </label>
                  <DateDMYInput
                    id="tx-date"
                    data-testid="tx-date"
                    required
                    value={(form.date || "").slice(0, 10)}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
                  />
                  <p className="text-[11px] text-ink-soft mt-1">
                    Defaults to today. Select a past date for historical treatments.
                  </p>
                </div>

                <button data-testid="tx-submit" className="w-full btn-primary text-sm">Save</button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="tx-summary-cards">
        {[
          { key: "planned",   label: "Planned",     value: counts.planned, tint: "#5B6660" },
          { key: "in_prog",   label: "In progress", value: counts.inProg,  tint: "#D06A42" },
          { key: "completed", label: "Completed",   value: counts.done,    tint: "#2D6A4F" },
          { key: "overdue",   label: "Overdue",     value: counts.overdue, tint: "#8A2D2D" },
        ].map((c) => (
          <div key={c.key} data-testid={`tx-card-${c.key}`}
               className="card-widget p-4 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ink-soft">{c.label}</div>
              <div className="font-display text-3xl mt-1" style={{ color: c.tint }}>{c.value}</div>
            </div>
            <div className="w-1 h-10 rounded-full" style={{ background: c.tint + "40" }} />
          </div>
        ))}
      </div>

      {/* Quick tabs */}
      <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="tx-tabs">
        {[
          { k: "all",         l: "All",              n: counts.total },
          { k: "today",       l: "Today",            n: null },
          { k: "scheduled",   l: "Scheduled",        n: null },
          { k: "in_progress", l: "In progress",      n: counts.inProg },
          { k: "completed",   l: "Completed",        n: counts.done },
          { k: "cancelled",   l: "Cancelled",        n: null },
          { k: "lab",         l: "Lab cases",        n: null },
          { k: "multi",       l: "Multi-visit",      n: null },
          { k: "high",        l: "High priority",    n: null },
        ].map((t) => {
          const on = tab === t.k;
          return (
            <button
              key={t.k}
              type="button"
              data-testid={`tx-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                on ? "bg-ink text-linen border-ink" : "border-[#E8EAE9] text-ink-mid hover:bg-linen"
              }`}
            >
              {t.l}{t.n != null ? <span className="opacity-70 ml-1">· {t.n}</span> : null}
            </button>
          );
        })}
      </div>

      {/* Filter bar */}
      <div className="card-widget p-3 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
            <input
              data-testid="tx-search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search patient, procedure, tooth, notes…"
              className="w-full rounded-full border border-[#E8EAE9] pl-9 pr-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            data-testid="tx-toggle-filters"
            onClick={() => setShowFilters((v) => !v)}
            className={`text-xs px-3 py-2 rounded-full border ${
              showFilters ? "bg-ink text-linen border-ink" : "border-[#E8EAE9] text-ink-mid hover:bg-linen"
            } inline-flex items-center gap-1.5`}
          >
            <Filter className="w-3.5 h-3.5" /> Filters
          </button>
          <button
            type="button"
            data-testid="tx-export-csv"
            onClick={exportCsv}
            className="text-xs px-3 py-2 rounded-full border border-[#E8EAE9] text-ink-mid hover:bg-linen inline-flex items-center gap-1.5"
          >
            <FileDown className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3" data-testid="tx-filters">
            <select data-testid="tx-filter-doctor" value={filterDoctor} onChange={(e) => setFilterDoctor(e.target.value)}
              className="rounded-lg border border-[#E8EAE9] px-3 py-2 text-xs bg-white">
              <option value="">All doctors</option>
              {doctorList.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
            <select data-testid="tx-filter-proc" value={filterProc} onChange={(e) => setFilterProc(e.target.value)}
              className="rounded-lg border border-[#E8EAE9] px-3 py-2 text-xs bg-white">
              <option value="">All procedures</option>
              {uniqueProcedures.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select data-testid="tx-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-lg border border-[#E8EAE9] px-3 py-2 text-xs bg-white">
              <option value="">Any status</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select data-testid="tx-filter-priority" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}
              className="rounded-lg border border-[#E8EAE9] px-3 py-2 text-xs bg-white">
              <option value="">Any priority</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
            <DateDMYInput data-testid="tx-filter-date" value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="rounded-lg border border-[#E8EAE9] px-3 py-2 text-xs bg-white" />
            <input data-testid="tx-filter-tooth" value={filterTooth}
              onChange={(e) => setFilterTooth(e.target.value.trim())}
              placeholder="Tooth (FDI)"
              className="rounded-lg border border-[#E8EAE9] px-3 py-2 text-xs bg-white" />
            <label data-testid="tx-filter-multivisit"
                   className="col-span-1 flex items-center gap-2 text-xs text-ink-mid px-2 py-1.5 rounded-lg hover:bg-linen cursor-pointer">
              <input type="checkbox" checked={filterMultiVisit}
                     onChange={(e) => setFilterMultiVisit(e.target.checked)} />
              Multi-visit only
            </label>
            <label data-testid="tx-filter-lab"
                   className="col-span-1 flex items-center gap-2 text-xs text-ink-mid px-2 py-1.5 rounded-lg hover:bg-linen cursor-pointer">
              <input type="checkbox" checked={filterLab}
                     onChange={(e) => setFilterLab(e.target.checked)} />
              Lab required
            </label>
            <button type="button" data-testid="tx-filter-clear"
              onClick={() => { setFilterDoctor(""); setFilterProc(""); setFilterStatus(""); setFilterDate(""); setFilterTooth(""); setFilterPriority(""); setFilterMultiVisit(false); setFilterLab(false); setSearchQ(""); }}
              className="col-span-2 md:col-span-6 text-[11px] text-ink-mid hover:text-ink underline text-left">
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="card-widget p-3 mb-3 flex items-center gap-2 flex-wrap" data-testid="tx-bulk-bar">
          <span className="text-xs text-ink-mid">{selected.size} selected</span>
          <select
            data-testid="tx-bulk-assign-doctor"
            onChange={(e) => { if (e.target.value !== "__none__") bulkAssignDoctor(e.target.value); e.target.value = "__none__"; }}
            defaultValue="__none__"
            className="rounded-full border border-[#E8EAE9] px-3 py-1.5 text-xs bg-white">
            <option value="__none__">Assign doctor…</option>
            <option value="">— Clear —</option>
            {doctorList.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select
            data-testid="tx-bulk-status"
            onChange={(e) => { if (e.target.value) bulkStatus(e.target.value); e.target.value = ""; }}
            defaultValue=""
            className="rounded-full border border-[#E8EAE9] px-3 py-1.5 text-xs bg-white">
            <option value="">Change status…</option>
            <option value="planned">Planned</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            data-testid="tx-bulk-priority"
            onChange={(e) => { if (e.target.value) bulkPriority(e.target.value); e.target.value = ""; }}
            defaultValue=""
            className="rounded-full border border-[#E8EAE9] px-3 py-1.5 text-xs bg-white">
            <option value="">Set priority…</option>
            <option value="high">High</option>
            <option value="normal">Normal</option>
            <option value="low">Low</option>
          </select>
          {isAdmin && (
            <button
              data-testid="tx-bulk-delete"
              onClick={bulkDelete}
              className="text-xs px-3 py-1.5 rounded-full border border-[#F0A5A5] text-[#8B1E1E] hover:bg-[#FFF3F3] inline-flex items-center gap-1.5">
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          )}
          <button
            data-testid="tx-bulk-clear"
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-ink-mid hover:text-ink inline-flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>
        </div>
      )}

      {/* Data table */}
      <div className="card-widget overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ink-soft overline">
              <th className="px-3 py-3 font-normal w-8">
                <input type="checkbox" data-testid="tx-select-all"
                  checked={paged.length > 0 && paged.every((t) => selected.has(t.id))}
                  onChange={toggleAllOnPage} />
              </th>
              <th className="px-3 py-3 font-normal">Patient</th>
              <th className="px-3 py-3 font-normal">Procedure</th>
              <th className="px-3 py-3 font-normal">Tooth</th>
              <th className="px-3 py-3 font-normal">Assigned doctor</th>
              <th className="px-3 py-3 font-normal whitespace-nowrap">Next visit</th>
              <th className="px-3 py-3 font-normal">Status</th>
              <th className="px-3 py-3 font-normal text-right">Cost</th>
              <th className="px-3 py-3 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#EEEDE7]">
            {paged.map((t) => {
              const expanded = expandedId === t.id;
              const isSel    = selected.has(t.id);
              return (
              <Fragment key={t.id}>
              <tr data-testid={`tx-row-${t.id}`} className={`hover:bg-linen/40 transition-colors ${isSel ? "bg-linen/60" : ""}`}>
                <td className="px-3 py-3">
                  <input type="checkbox" data-testid={`tx-select-${t.id}`}
                    checked={isSel} onChange={() => toggleRow(t.id)} />
                </td>
                <td className="px-3 py-3 font-medium truncate max-w-[180px]">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : t.id)}
                    data-testid={`tx-expand-${t.id}`}
                    className="inline-flex items-center gap-1.5 hover:text-ink transition-colors"
                    aria-expanded={expanded}
                  >
                    <ChevronRight className={`w-3.5 h-3.5 text-ink-soft transition-transform ${expanded ? "rotate-90" : ""}`} />
                    <span className="truncate">{pname(t.patient_id)}</span>
                  </button>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{t.procedure}</span>
                    {t.priority && t.priority !== "normal" && (
                      <span
                        data-testid={`tx-priority-${t.id}`}
                        className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded font-medium"
                        style={{ background: PRIORITY_STYLES[t.priority]?.bg, color: PRIORITY_STYLES[t.priority]?.fg }}
                      >
                        {PRIORITY_STYLES[t.priority]?.label || t.priority}
                      </span>
                    )}
                    {t.send_to_lab && (
                      <span data-testid={`tx-lab-${t.id}`}
                            className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#E9E4F6] text-[#4A3F8A]">
                        Lab
                      </span>
                    )}
                  </div>
                  {t.variant && <div className="text-[10px] text-ink-soft">{t.variant}</div>}
                  {(t.sessions_planned || 0) >= 2 && (
                    <div className="mt-1.5 flex items-center gap-2" data-testid={`tx-progress-${t.id}`}>
                      <div className="h-1 flex-1 rounded-full bg-linen overflow-hidden max-w-[100px]">
                        <div className="h-full bg-[#D06A42]"
                             style={{ width: `${Math.min(100, Math.round(((t.sessions_completed || 0) / t.sessions_planned) * 100))}%` }} />
                      </div>
                      <span className="text-[10px] text-ink-soft whitespace-nowrap font-mono">
                        Visit {t.sessions_completed || 0}/{t.sessions_planned}
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-ink-mid font-mono">{t.tooth || "—"}</td>
                <td className="px-3 py-3 text-ink-mid">
                  {t.doctor_id ? (t.doctor_name || dname(t.doctor_id)) : <span className="text-[11px] italic text-ink-soft">Unassigned</span>}
                </td>
                <td className="px-3 py-3 text-ink-mid whitespace-nowrap font-mono text-xs">
                  {t.next_session_start ? new Date(t.next_session_start).toLocaleString("en-IN", {day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"}) : (t.date?.slice(0, 10) || "—")}
                </td>
                <td className="px-3 py-3">
                  <span className={`text-[10px] px-2.5 py-1 rounded-full uppercase tracking-widest ${STATUS_STYLES[t.status] || "bg-linen text-ink"}`}>
                    {t.status.replace("_", " ")}
                  </span>
                </td>
                <td className="px-3 py-3 text-right font-display whitespace-nowrap">₹{t.cost.toLocaleString("en-IN")}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-1 flex-nowrap whitespace-nowrap">
                    {t.status !== "completed" && (
                      <button data-testid={`mark-completed-${t.id}`} onClick={() => markCompleted(t)}
                        title="Mark completed"
                        className="text-[11px] px-2 py-1 rounded-md border border-pine/20 text-ink hover:bg-linen">
                        ✓ Done
                      </button>
                    )}
                    <button data-testid={`gen-receipt-${t.id}`} onClick={() => generateReceipt(t)}
                      title="Generate GST invoice"
                      className="text-[11px] px-2 py-1 rounded-md border border-pine/20 text-ink hover:bg-linen inline-flex items-center gap-1">
                      <Receipt className="w-3 h-3" /> Invoice
                    </button>
                    {canDelete && (
                      <button
                        data-testid={`delete-treatment-${t.id}`}
                        onClick={async () => {
                          if (!window.confirm(`Delete this treatment for ${pname(t.patient_id)}? This cannot be undone.`)) return;
                          try { await api.delete(`/treatments/${t.id}`); toast.success("Treatment deleted"); load(); }
                          catch (err) { toast.error(apiError(err, "Delete failed")); }
                        }}
                        title="Delete treatment"
                        className="text-[11px] p-1.5 rounded-md border border-[#F0A5A5] text-[#8B1E1E] hover:bg-[#FFF3F3]"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              {expanded && (
                <tr data-testid={`tx-sessions-row-${t.id}`}>
                  <td colSpan={9} className="p-0">
                    <SessionsPanel treatment={t} onSessionChange={load} />
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-ink-soft">No treatments match the current filters.</td></tr>}
          </tbody>
        </table>

        {/* Pagination footer */}
        {filtered.length > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap px-3 py-3 border-t border-[#EEEDE7]" data-testid="tx-pagination">
            <div className="text-[11px] text-ink-mid">
              Showing <span className="font-medium text-ink">{pageStart + 1}</span>–<span className="font-medium text-ink">{pageEnd}</span> of <span className="font-medium text-ink">{filtered.length}</span>
            </div>
            <div className="flex items-center gap-2">
              <select
                data-testid="tx-page-size"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded-full border border-[#E8EAE9] px-3 py-1 text-xs bg-white"
              >
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <button
                data-testid="tx-page-prev"
                disabled={pageIndex === 0}
                onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                className="text-xs px-3 py-1 rounded-full border border-[#E8EAE9] hover:bg-linen disabled:opacity-40 disabled:cursor-not-allowed"
              >Prev</button>
              {Array.from({ length: pageCount }, (_, i) => i).slice(
                Math.max(0, pageIndex - 2),
                Math.min(pageCount, pageIndex + 3)
              ).map((i) => (
                <button
                  key={i}
                  data-testid={`tx-page-${i}`}
                  onClick={() => setPageIndex(i)}
                  className={`text-xs w-7 h-7 rounded-full transition ${
                    i === pageIndex ? "bg-ink text-linen" : "border border-[#E8EAE9] hover:bg-linen"
                  }`}
                >{i + 1}</button>
              ))}
              <button
                data-testid="tx-page-next"
                disabled={pageIndex >= pageCount - 1}
                onClick={() => setPageIndex((i) => Math.min(pageCount - 1, i + 1))}
                className="text-xs px-3 py-1 rounded-full border border-[#E8EAE9] hover:bg-linen disabled:opacity-40 disabled:cursor-not-allowed"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
